import styles from "./edit-peripheral.module.css";
import { inject, observer } from "mobx-react";
import { PeripheralDataStore } from "../../store/store";
import BusInput from "./components/bus-input";
import AddressInput from "./components/address-input";
import SubaddressInput from "./components/subaddress-input";
import MacAddressInput from "./components/mac-address-input";
import IpAddressInput from "./components/ip-address-input";
import UsedToggle from "./components/used-toggle";
import CancelButton from "../../../../components/buttons/CancelButton";
import SaveButton from "../../../../components/buttons/SaveButton";
import UsedInfoIcon from "./components/used-info-icon";
import { useTranslation } from "react-i18next";
import { Ref, useEffect, useRef } from "react";
import { scrollIntoViewAndAlign } from "../../../../utils/scroll";
import { runInAction } from "mobx";
import { PeripheralData } from "../../store/types";
import LocationSelector, {
  LocationItem,
} from "../../../../components/location-selector/LocationSelector";
import LocationSelectorStore from "../../../../store/location-selector-store";

interface EditPeripheralProps {
  peripheralDataStore?: PeripheralDataStore;
  locationSelectorStore?: LocationSelectorStore;
  saveLocked?: boolean;
  onSaveClick(): void;
  onCancelClick(): void;
}

function EditPeripheral(props: EditPeripheralProps) {
  const editComponentElement: Ref<any> = useRef(null);
  let lockedUsedToggle: boolean = false;
  const peripheralDataStore = props.peripheralDataStore!;
  const locationSelectorStore = props.locationSelectorStore!;
  const { saveLocked, onSaveClick, onCancelClick } = props;
  const { t } = useTranslation("common");
  const peripheralList = peripheralDataStore.peripherals;
  const selectedLocationName =
    peripheralDataStore.selectedPeripheral.locationName;
  const selectedAreaName = peripheralDataStore.selectedPeripheral.areaName;
  const selectedLocationId = peripheralDataStore.selectedPeripheral.locationId;
  const selectedPeripheralId =
    peripheralDataStore.selectedPeripheral.peripheralId;
  const { errorMessages } = peripheralDataStore;

  useEffect(() => {
    scrollIntoViewAndAlign(editComponentElement.current);
    return function unmount() {
      runInAction(() => {
        peripheralDataStore.errors = null;
        peripheralDataStore.errorMessages = null;
        peripheralDataStore.setValidated(true);
      });
    };
  }, []);

  const locationList = () => {
    let dropdownData: any = locationSelectorStore.selectedRoomList.map(
      (aLocation) => {
        return {
          locationId: aLocation.id,
          locationName: aLocation.name,
          areaName: aLocation.path,
        };
      }
    );

    return dropdownData;
  };

  const setSelectedPeripheralLocation = (value: LocationItem) => {
    if (value === null) {
      peripheralDataStore!.setSelectedPeripheralLocationData(0, "", "");
      peripheralDataStore!.validateSelectedPeripheral();
      return;
    }

    peripheralDataStore.setSelectedPeripheralLocationData(
      value.locationId!,
      value.locationName,
      value.areaName
    );

    peripheralDataStore.validateSelectedPeripheral("locationId");
  };

  const existingPeripheralRoom = peripheralList.find(
    (peripheral: PeripheralData) => {
      if (
        selectedLocationName === peripheral.locationName &&
        selectedAreaName === peripheral.areaName &&
        selectedPeripheralId !== peripheral.peripheralId
      ) {
        return true;
      }

      return false;
    }
  );

  if (selectedLocationId === undefined) {
    lockedUsedToggle = false;
  } else if (existingPeripheralRoom === undefined) {
    lockedUsedToggle = true;

    peripheralDataStore.selectedPeripheral.isUsed = true;
  } else {
    const existingActivePeripheral = peripheralList.find(
      (peripheral: PeripheralData) => {
        if (
          selectedLocationName === peripheral.locationName &&
          selectedAreaName === peripheral.areaName &&
          selectedPeripheralId !== peripheral.peripheralId &&
          peripheral.isUsed
        ) {
          return true;
        }

        return false;
      }
    );

    if (existingActivePeripheral) {
      lockedUsedToggle = false;
    } else lockedUsedToggle = true;
  }

  const render = () => {
    return (
      <tr className={styles["edit-peripheral"]} ref={editComponentElement}>
        <td></td>

        <td>
          <div className={styles["column"]}>
            <LocationSelector
              mandatory
              label={t("Room").toUpperCase()}
              selectedId={peripheralDataStore.selectedPeripheral.locationId!}
              locationList={locationList()}
              onValueChange={(value: LocationItem) => {
                setSelectedPeripheralLocation(value);
              }}
              errorMessage={
                (errorMessages &&
                  errorMessages["locationId"] &&
                  t("Required information")) ||
                ""
              }
            />
          </div>
        </td>

        <td>
          <div className={styles["column"]}>
            <BusInput />
          </div>
        </td>

        <td>
          <div className={styles["column"]}>
            <AddressInput />
          </div>
        </td>

        <td>
          <div className={styles["column"]}>
            <SubaddressInput />
          </div>
        </td>

        <td>
          <div className={styles["column"]}>
            <MacAddressInput />
          </div>
        </td>

        <td>
          <div className={styles["column"]}>
            <IpAddressInput />
          </div>
        </td>

        <td>
          <div className={styles["column"]}>
            <div className={styles["used-column-content"]}>
              <UsedToggle lockedToggle={lockedUsedToggle} />

              <UsedInfoIcon />
            </div>
          </div>
        </td>

        <td className={styles["action-column"]}>
          <div className={styles["action-buttons-container"]}>
            <div className={styles["action-buttons"]}>
              <CancelButton
                onClick={() => {
                  onCancelClick();
                }}
              >
                {t("Cancel")}
              </CancelButton>

              <SaveButton
                onClick={() => {
                  onSaveClick();
                }}
                disabled={saveLocked}
              >
                {t("Save")}
              </SaveButton>
            </div>
          </div>
        </td>
      </tr>
    );
  };

  return render();
}

export default inject(
  "peripheralDataStore",
  "locationSelectorStore"
)(observer(EditPeripheral));
